<script lang="ts">
	import { onMount } from 'svelte';
	import { toast } from 'svelte-sonner';
	import { browser } from '$app/environment';
	import { authStore } from '$lib/stores/auth';
	import { userTimezone } from '$lib/utils/userTimezone';
	import { isValidTimezone } from '$lib/utils/timezoneList';
	import { saveTimezone } from '$lib/features/settings/logic/timezone';
	import { shouldSuggestTimezone } from './TimezoneSuggestPrompt.logic';
	import * as m from '$lib/paraglide/messages';

	const DISMISS_KEY = 'tz-suggest-dismissed';

	// Once per session — the subscription fires on every auth-state change.
	let prompted = false;

	function dismiss() {
		if (browser) localStorage.setItem(DISMISS_KEY, '1');
	}

	async function applyDetected() {
		try {
			if (!(await saveTimezone(userTimezone))) return;
			toast.success(m.settings_timezone_save_success());
		} catch (err) {
			console.error('Failed to save timezone', err);
			toast.error(m.settings_timezone_save_error());
		}
	}

	function maybePrompt(saved: string | null | undefined) {
		if (!browser || prompted) return;
		const dismissed = localStorage.getItem(DISMISS_KEY) === '1';
		if (
			!shouldSuggestTimezone({
				saved,
				detected: userTimezone,
				detectedIsValid: isValidTimezone(userTimezone),
				dismissed
			})
		) {
			return;
		}

		prompted = true;
		// Offer the zone — never write it silently. Writing only happens on click.
		toast(m.settings_timezone_suggest_title(), {
			description: m.settings_timezone_suggest_description({ timezone: userTimezone }),
			action: {
				label: m.settings_timezone_suggest_action(),
				onClick: applyDetected
			},
			onDismiss: dismiss,
			onAutoClose: dismiss,
			duration: Infinity
		});
	}

	onMount(() => {
		const unsubscribe = authStore.subscribe((state) => {
			if (state.user) maybePrompt(state.user.timezone);
		});
		return unsubscribe;
	});
</script>
